import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { subjectsWizard, ApiError } from '@/api/client';
import { normalisePhone, PHONE_HINT } from '@/lib/phone';

const BACKEND_TO_FIELD: Record<string, keyof DemoErrors> = {
  height_ft: 'height',
  height_in: 'height',
  weight_lbs: 'weight',
  sex: 'sex',
  gender: 'gender',
  ethnicity: 'ethnicity',
  race: 'race',
  preferred_language: 'language',
};

const ETHNICITY_OPTIONS = ['Hispanic or Latino', 'Not Hispanic or Latino', 'Unknown', 'Declined to answer'];
const RACE_OPTIONS = [
  'American Indian or Alaska Native',
  'Asian',
  'Black or African American',
  'Native Hawaiian or Other Pacific Islander',
  'White',
  'Multiple',
  'Unknown',
  'Declined to answer',
];
const LANGUAGE_OPTIONS = [
  'English',
  'Spanish / Español',
  'French / Français',
  'Chinese (Simplified) / 中文（简体）',
  'Chinese (Traditional) / 中文（繁體）',
  'Vietnamese / Tiếng Việt',
  'Korean / 한국어',
  'Arabic / العربية',
  'Tagalog / Filipino',
  'Portuguese / Português',
  'Hindi / हिन्दी',
  'Russian / Русский',
  'Japanese / 日本語',
  'German / Deutsch',
  'Italian / Italiano',
  'Other',
];

interface DemoErrors {
  sex?: string;
  gender?: string;
  height?: string;
  weight?: string;
  ethnicity?: string;
  race?: string;
  language?: string;
  email?: string;
  phone?: string;
  contact?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function Step3Demographics({
  subjectId,
  onComplete,
  triggerRef,
  saveTrigger,
  onPendingChange,
  onSavePendingChange,
  initialSex,
  initialGender,
  initialHeightFt,
  initialHeightIn,
  initialWeightLbs,
  initialEthnicity,
  initialRace,
  initialLanguage,
  initialEmail,
  initialPhone,
}: {
  subjectId: string;
  onComplete: () => void;
  onSaveAndResume?: () => void;
  triggerRef?: React.MutableRefObject<(() => void) | null>;
  saveTrigger?: React.MutableRefObject<(() => void) | null>;
  onPendingChange?: (pending: boolean) => void;
  onSavePendingChange?: (pending: boolean) => void;
  initialSex?: string | null;
  initialGender?: string | null;
  initialHeightFt?: number | null;
  initialHeightIn?: number | null;
  initialWeightLbs?: number | null;
  initialEthnicity?: string | null;
  initialRace?: string | null;
  initialLanguage?: string | null;
  initialEmail?: string | null;
  initialPhone?: string | null;
}) {
  const qc = useQueryClient();

  const [sex, setSex] = useState(() => initialSex ?? '');
  const [gender, setGender] = useState(() => initialGender ?? '');
  const [heightFt, setHeightFt] = useState(() => (initialHeightFt != null ? String(initialHeightFt) : ''));
  const [heightIn, setHeightIn] = useState(() => (initialHeightIn != null ? String(initialHeightIn) : ''));
  const [weightLbs, setWeightLbs] = useState(() => (initialWeightLbs != null ? String(initialWeightLbs) : ''));
  const [ethnicity, setEthnicity] = useState(() => initialEthnicity ?? '');
  const [race, setRace] = useState(() => initialRace ?? '');
  const [language, setLanguage] = useState(() => initialLanguage ?? '');
  // Year only. The full date of birth is collected on the consent document
  // for signer identity and deliberately never reaches the subject record.
  const [email, setEmail] = useState(() => initialEmail ?? '');
  const [phone, setPhone] = useState(() => initialPhone ?? '');
  const [errors, setErrors] = useState<DemoErrors>({});

  function validate(): boolean {
    const next: DemoErrors = {};
    if (!sex) next.sex = 'Sex is required.';
    if (!gender) next.gender = 'Gender identity is required.';
    if (!heightFt) {
      next.height = 'Height is required.';
    } else {
      const ft = parseInt(heightFt, 10);
      if (isNaN(ft) || !Number.isInteger(ft)) next.height = 'Feet must be a whole number.';
      else if (ft < 0 || ft > 9) next.height = 'Feet must be between 0 and 9.';
    }
    if (heightIn) {
      const inches = parseFloat(heightIn);
      if (isNaN(inches) || inches < 0 || inches > 11.9)
        next.height = next.height ?? 'Inches must be between 0 and 11.9.';
    }
    if (!weightLbs) {
      next.weight = 'Weight is required.';
    } else {
      const lbs = parseFloat(weightLbs);
      if (isNaN(lbs) || lbs <= 0) next.weight = 'Weight must be a positive number.';
    }
    if (!ethnicity) next.ethnicity = 'Ethnicity is required.';
    if (!race) next.race = 'Race is required.';
    if (!language) next.language = 'Preferred language is required.';
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    if (!trimmedEmail && !trimmedPhone) {
      // Phase 1 Item 4 — remote-sign (Item 5) needs at least one contact
      // channel; enforce it here so subjects don't reach the sign flow
      // with no way to reach them.
      next.contact = 'At least one of personal email or phone is required.';
    }
    if (trimmedEmail && !EMAIL_RE.test(trimmedEmail)) {
      next.email = 'Enter a valid email address.';
    }
    if (trimmedPhone && !normalisePhone(trimmedPhone)) {
      next.phone = PHONE_HINT;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSaveError(e: Error) {
    if (e instanceof ApiError && e.code === 'VALIDATION_ERROR') {
      const issues = (e.details as { issues?: Array<{ path: string[]; message: string }> } | null)?.issues;
      if (issues?.length) {
        const fieldErrors: DemoErrors = {};
        const unmapped: string[] = [];
        for (const issue of issues) {
          const key = issue.path[0];
          const field = key !== undefined ? BACKEND_TO_FIELD[key] : undefined;
          if (field) fieldErrors[field] = issue.message;
          else unmapped.push(issue.message);
        }
        if (Object.keys(fieldErrors).length) setErrors((p) => ({ ...p, ...fieldErrors }));
        if (unmapped.length) toast.error(`Save failed: ${unmapped.join(', ')}`);
        return;
      }
    }
    toast.error(`Save failed: ${e.message}`);
  }

  function buildBody() {
    return {
      sex: sex || null,
      gender: gender || null,
      height_ft: heightFt ? parseInt(heightFt, 10) || null : null,
      height_in: heightIn ? parseFloat(heightIn) || null : null,
      weight_lbs: weightLbs ? parseFloat(weightLbs) || null : null,
      ethnicity: ethnicity || null,
      race: race || null,
      preferred_language: language || null,
      // No date_of_birth_year: the Year of birth field was removed (16 Oct
      // 2026); the full date of birth is captured on the consent step.
    };
  }

  function buildContactBody(): { email_personal?: string; phone_e164?: string } {
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    const body: { email_personal?: string; phone_e164?: string } = {};
    // Only send fields the user actually filled — Phase 1 Item 4 handler
    // rejects a fully-empty payload. Draft-save may still succeed on
    // demographics alone; contact save is skipped in that case.
    if (trimmedEmail) body.email_personal = trimmedEmail;
    // Always send E.164 — the column and the OTP sender both require it,
    // regardless of the US-style formatting the coordinator typed.
    if (trimmedPhone) body.phone_e164 = normalisePhone(trimmedPhone) ?? trimmedPhone;
    return body;
  }

  async function submitAll() {
    // Save demographics first, then contact info. Sequential because
    // both write to the subject row and we want a stable order in the
    // audit trail; contact info is a distinct PhiEvent so it must be
    // audited separately.
    await subjectsWizard.recordDemographics(subjectId, buildBody());
    const contact = buildContactBody();
    if (Object.keys(contact).length > 0) {
      await subjectsWizard.recordContactInfo(subjectId, contact);
    }
  }

  const save = useMutation({
    mutationFn: submitAll,
    onSuccess: () => {
      toast.success('Demographics saved.');
      onComplete();
    },
    onError: handleSaveError,
  });

  const saveDraft = useMutation({
    mutationFn: submitAll,
    onSuccess: () => {
      toast.success('Draft saved — click Exit to leave and resume later.');
      qc.invalidateQueries({ queryKey: ['subject', subjectId] });
    },
    onError: handleSaveError,
  });

  useEffect(() => {
    if (triggerRef) triggerRef.current = () => { if (validate()) save.mutate(); };
    if (saveTrigger) saveTrigger.current = () => saveDraft.mutate();
  });

  useEffect(() => {
    onPendingChange?.(save.isPending);
  }, [save.isPending, onPendingChange]);

  useEffect(() => {
    onSavePendingChange?.(saveDraft.isPending);
  }, [saveDraft.isPending, onSavePendingChange]);

  const req = <span className="text-red-600">*</span>;

  return (
    <div className="space-y-5">
      <h2 className="text-base font-semibold text-ink-primary">Demographics</h2>
      <p className="text-xs text-ink-muted">
        All fields are required. Demographic data follows OMB / FDA standard categories.
      </p>

      {/* Sex + Gender */}
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs">
          <span className="font-medium text-ink-secondary">Sex (biological) {req}</span>
          <select
            value={sex}
            onChange={(e) => { setSex(e.target.value); setErrors((p) => ({ ...p, sex: undefined })); }}
            className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
          >
            <option value="">— select —</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Intersex">Intersex</option>
            <option value="Unknown">Unknown</option>
            <option value="Declined to answer">Declined to answer</option>
          </select>
          {errors.sex && <div className="mt-1 text-[11px] text-red-600">{errors.sex}</div>}
        </label>

        <label className="block text-xs">
          <span className="font-medium text-ink-secondary">Gender identity {req}</span>
          <select
            value={gender}
            onChange={(e) => { setGender(e.target.value); setErrors((p) => ({ ...p, gender: undefined })); }}
            className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
          >
            <option value="">— select —</option>
            <option value="Man">Man</option>
            <option value="Woman">Woman</option>
            <option value="Non-binary">Non-binary</option>
            <option value="Transgender man">Transgender man</option>
            <option value="Transgender woman">Transgender woman</option>
            <option value="Prefer not to say">Prefer not to say</option>
            <option value="Other">Other</option>
          </select>
          {errors.gender && <div className="mt-1 text-[11px] text-red-600">{errors.gender}</div>}
        </label>
      </div>

      {/* Height + Weight */}
      <div className="grid grid-cols-2 gap-3">
        <div className="block text-xs">
          <span className="font-medium text-ink-secondary">Height {req}</span>
          <div className="mt-1 flex items-center gap-1.5">
            <input
              type="number"
              min={1}
              max={9}
              step={1}
              value={heightFt}
              onChange={(e) => { setHeightFt(e.target.value); setErrors((p) => ({ ...p, height: undefined })); }}
              placeholder="5"
              className="w-16 rounded border border-surface-border px-2 py-2 text-sm"
            />
            <span className="shrink-0 text-[11px] text-ink-muted">ft</span>
            <input
              type="number"
              min={0}
              max={11}
              step={0.5}
              value={heightIn}
              onChange={(e) => { setHeightIn(e.target.value); setErrors((p) => ({ ...p, height: undefined })); }}
              placeholder="10"
              className="w-16 rounded border border-surface-border px-2 py-2 text-sm"
            />
            <span className="shrink-0 text-[11px] text-ink-muted">in</span>
          </div>
          {errors.height && <div className="mt-1 text-[11px] text-red-600">{errors.height}</div>}
        </div>

        <label className="block text-xs">
          <span className="font-medium text-ink-secondary">Weight {req}</span>
          <div className="mt-1 flex items-center gap-1.5">
            <input
              type="number"
              min={1}
              max={1500}
              step={0.5}
              value={weightLbs}
              onChange={(e) => { setWeightLbs(e.target.value); setErrors((p) => ({ ...p, weight: undefined })); }}
              placeholder="e.g. 155"
              className="w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
            <span className="shrink-0 text-[11px] text-ink-muted">lbs</span>
          </div>
          {errors.weight && <div className="mt-1 text-[11px] text-red-600">{errors.weight}</div>}
        </label>
      </div>

      {/* Ethnicity + Race */}
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs">
          <span className="font-medium text-ink-secondary">Ethnicity {req}</span>
          <select
            value={ethnicity}
            onChange={(e) => { setEthnicity(e.target.value); setErrors((p) => ({ ...p, ethnicity: undefined })); }}
            className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
          >
            <option value="">— select —</option>
            {ETHNICITY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {errors.ethnicity && <div className="mt-1 text-[11px] text-red-600">{errors.ethnicity}</div>}
        </label>

        <label className="block text-xs">
          <span className="font-medium text-ink-secondary">Race {req}</span>
          <select
            value={race}
            onChange={(e) => { setRace(e.target.value); setErrors((p) => ({ ...p, race: undefined })); }}
            className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
          >
            <option value="">— select —</option>
            {RACE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {errors.race && <div className="mt-1 text-[11px] text-red-600">{errors.race}</div>}
        </label>
      </div>

      {/* Year of birth removed (client, 16 Oct 2026): never used by any check,
          and it disagreed with the consent date of birth (Step 3, required),
          which is the one record of it. */}

      {/* Preferred language */}
      <label className="block text-xs">
        <span className="font-medium text-ink-secondary">Preferred language {req}</span>
        <select
          value={language}
          onChange={(e) => { setLanguage(e.target.value); setErrors((p) => ({ ...p, language: undefined })); }}
          className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
        >
          <option value="">— select —</option>
          {LANGUAGE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {errors.language && <div className="mt-1 text-[11px] text-red-600">{errors.language}</div>}
      </label>

      {/* Contact info (Phase 1 Item 4 — required for remote sign) */}
      <div className="mt-2 rounded border border-surface-border bg-surface-muted/40 p-3">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-semibold text-ink-primary">Contact information</span>
          <span className="text-[11px] text-ink-muted">Encrypted PHI · used for remote consent</span>
        </div>
        <p className="mb-3 text-[11px] text-ink-muted">
          At least one channel is required so the subject can receive a remote signing link.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Personal email</span>
            <input
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((p) => ({ ...p, email: undefined, contact: undefined }));
              }}
              placeholder="subject@example.com"
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
            {errors.email && <div className="mt-1 text-[11px] text-red-600">{errors.email}</div>}
          </label>
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Mobile phone</span>
            <input
              type="tel"
              autoComplete="off"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setErrors((p) => ({ ...p, phone: undefined, contact: undefined }));
              }}
              placeholder="(415) 555-1234"
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
            {errors.phone ? (
              <div className="mt-1 text-[11px] text-red-600">{errors.phone}</div>
            ) : (
              phone.trim() &&
              normalisePhone(phone) && (
                // Show what actually gets stored, so the coordinator can see
                // their US-formatted entry resolved to a country code.
                <div className="mt-1 text-[11px] text-ink-muted">
                  Saved as {normalisePhone(phone)}
                </div>
              )
            )}
          </label>
        </div>
        {errors.contact && <div className="mt-2 text-[11px] text-red-600">{errors.contact}</div>}
      </div>
    </div>
  );
}
